/* eslint-disable react/destructuring-assignment */
import Text from '@deprecated-ui/common/Text';
import color from '@deprecated-ui/_style/color';
import Flex from '@deprecated-ui/common/Flex';
import Iconmoon from '@deprecated-ui/common/Iconmoon';
import Image from 'global/Image';
import If from 'global/Common/If';
import { useRouter } from 'next/router';
import { useState, createRef, useRef, RefObject, useEffect } from 'react';
import ModalOTPOptions from './ModalOTPOptions';
import { InputOTP, InputOTPContainer, Button } from './styled';
import CountDown, { CountdownRef } from './SectionOTPCountdown';
import { OTPConfig, OTPData } from './hooks/useChangePassword';
import { CHANNEL_OTP } from './config';
import ModalBlockedReqOTP from './ModalBlockedReqOTP';

const KEY_CODE_BACKSPACE = 8;
const KEY_CODE_DELETE = 46;
const KEY_CODE_ENTER = 13;

interface SectionOTPField {
  onSubmit: () => void;
  onRequestOTP: () => Promise<void>;
  requestOTPOnload?: boolean;
  preventSubmit?: boolean;
  multipleOTPMethodAvailable?: boolean;
  submitText: string;
  setOtpConfig: (otpConfig: OTPConfig) => void;
  otpData: OTPData;
  otpConfig: OTPConfig;
  otpLength: number;
  error: string;
  resetError: () => void;
  blockedOtpCtaText?: string;
  blockedOtpCtaCallback?: () => void;
  otpOptions?: {
    value: CHANNEL_OTP;
    icon: string;
    text: string;
    subText: string;
  }[];
}

const SectionTitle = ({ title, subTitle, img }) => (
  <>
    <Image
      src={img}
      width="70px"
      height="70px"
      alt="password-email"
      style={{ marginBottom: '18px' }}
    />
    <div style={{ paddingBottom: '18px' }}>
      <Text weight={700}>{title}</Text>
      <Text
        color="#858585"
        // @ts-expect-error
        pt="10px"
        dangerouslySetInnerHTML={{ __html: subTitle }}
      />
    </div>
  </>
);

const SectionOTPField = ({
  onSubmit,
  onRequestOTP,
  requestOTPOnload = true,
  multipleOTPMethodAvailable = true,
  preventSubmit,
  submitText,
  setOtpConfig,
  otpConfig,
  otpLength,
  otpData,
  error,
  resetError,
  blockedOtpCtaText,
  blockedOtpCtaCallback,
  otpOptions,
}: SectionOTPField) => {
  const router = useRouter();
  const countdownRef = useRef<CountdownRef>(null);
  const inputRef = useRef<RefObject<HTMLInputElement>[]>(
    [...Array(otpLength)].map(() => createRef()),
  );
  const [inputValue, setInputValue] = useState([...Array(otpLength)]);
  const [showModalOptions, setShowModalOptions] = useState(false);
  const [showModalBlocked, setShowModalBlocked] = useState(false);
  const [countDownEnd, setCountDownEnd] = useState(false);

  const onResetInput = () => {
    setInputValue([...inputValue].map(() => ''));
    inputRef.current[0].current.focus();
  };

  const requestOtp = async () => {
    try {
      await onRequestOTP();
      onResetInput();
    } catch (e) {
      if (e?.cause?.errorType === 'OTP_LIMIT_EXCEEDED') {
        setShowModalBlocked(true);
      }
    }
  };

  const onChangeInput = (e) => (index) => {
    const value = e.target?.value || '';

    if (e.keyCode !== KEY_CODE_BACKSPACE && e.keyCode !== KEY_CODE_DELETE) {
      const sanitizedInput = /\D/.test(value) ? '' : value[0];
      setInputValue(
        [...inputValue].map((v, i) => (i === index ? sanitizedInput : v)),
      );

      if (index < otpLength - 1 && Boolean(sanitizedInput)) {
        inputRef.current[index + 1].current.focus();
      }
    }

    if (e.keyCode === KEY_CODE_BACKSPACE) {
      setInputValue([...inputValue].map((v, i) => (i === index ? '' : v)));
      if (index > 0 && index <= otpLength - 1) {
        inputRef.current[index - 1].current.focus();
      }
    }

    if (e.keyCode === KEY_CODE_ENTER && !preventSubmit) {
      onSubmit();
    }
  };

  const renderTitle = (option: string) => {
    const titleContent = {
      [CHANNEL_OTP.EMAIL]: (
        <SectionTitle
          title="Enter Your Email Verification Code"
          subTitle={`Verification code has been sent to your email
            <b>${otpData.target}</b>
          `}
          img="/icon/password-email.svg"
        />
      ),
      [CHANNEL_OTP.WHATSAPP]: (
        <SectionTitle
          title="Enter Your Whatsapp Verification Code"
          subTitle={`Verification code has been sent to <b>${otpData.target}</b>`}
          img="/icon/password-wa.svg"
        />
      ),
      [CHANNEL_OTP.SMS]: (
        <SectionTitle
          title="Enter Your SMS Verification Code"
          subTitle={`Verification code has been sent to <b>${otpData.target}</b>`}
          img="/icon/password-sms.svg"
        />
      ),
    };

    return titleContent[option] || null;
  };

  useEffect(() => {
    resetError();
    setOtpConfig({
      channel: otpConfig.channel,
      otp: inputValue.join(''),
    });
  }, [inputValue]);

  useEffect(() => {
    if (requestOTPOnload) requestOtp();
  }, [requestOTPOnload]);

  useEffect(() => {
    resetError();
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      const handlePaste = (e) => {
        if (e?.clipboardData) {
          e.preventDefault();
          const pastedData = e?.clipboardData.getData('Text');

          setInputValue(
            [...pastedData.split('')].map((v, i) => {
              if (i >= otpLength) {
                return '';
              }
              const sanitizedInput = /\D/.test(v) ? '' : v;
              return sanitizedInput;
            }),
          );
        }
      };
      inputRef.current.forEach((c) => {
        c.current.addEventListener('paste', handlePaste);
      });

      return () => {
        inputRef.current.forEach((c) => {
          c.current?.removeEventListener('paste', handlePaste);
        });
      };
    }

    return () => {};
  }, [inputRef]);

  return (
    <>
      <ModalOTPOptions
        visible={showModalOptions}
        closeModal={() => setShowModalOptions(false)}
        onRequestOTP={requestOtp}
        initalSecond={
          countDownEnd
            ? 0
            : countdownRef?.current?.getTimeLeft() || otpData.remainingSecond
        }
        selectedOption={otpConfig.channel}
        setSelectedOption={(channel) => {
          setOtpConfig({
            channel,
            otp: channel !== otpConfig.channel ? '' : otpConfig.otp,
          });
        }}
        resetCountDown={() => setCountDownEnd(false)}
        otpOptions={otpOptions}
      />
      <ModalBlockedReqOTP
        visible={showModalBlocked}
        title={error}
        onBack={() =>
          blockedOtpCtaCallback !== undefined
            ? blockedOtpCtaCallback()
            : router.push('/profile/edit')
        }
        submitText={blockedOtpCtaText}
      />
      <Flex height="455px" direction="column">
        {renderTitle(otpConfig.channel)}
        <div style={{ width: '100%', position: 'relative' }}>
          {/* @ts-expect-error */}
          <Flex align="center" style={{ gap: 16 }} mb="10px">
            {[...Array(otpLength)].map((_e, i) => (
              <InputOTPContainer key={`input-token${i + 1}`}>
                <InputOTP
                  autoFocus={i === 0}
                  ref={inputRef.current[i]}
                  value={inputValue[i]}
                  onChange={(e) => onChangeInput(e)(i)}
                  onKeyDown={(e) => onChangeInput(e)(i)}
                  id={`input-email-otp-verify-${i + 1}`}
                  data-cy="confirm-otp-input-box"
                  style={{ fontWeight: 'bold' }}
                  autoComplete="off"
                />
              </InputOTPContainer>
            ))}
          </Flex>
          <If condition={Boolean(error)}>
            <Flex
              align="center"
              // @ts-expect-error
              pb="8px"
              style={{ visibility: error ? 'visible' : 'hidden' }}
              data-cy="new-password-error-validation"
            >
              <Iconmoon
                size="16px"
                className="icon-toolbar_error"
                color={color.sunsetOrange}
              />
              <Text
                color={color.sunsetOrange}
                size="12px"
                weight="400px"
                // @ts-expect-error
                pl="5px"
              >
                {error}
              </Text>
            </Flex>
          </If>

          <If condition={!countDownEnd && otpData.remainingSecond > 0}>
            {/* @ts-expect-error */}
            <Text color="#858585" pb="16px" pt="5px">
              Resend (
              <CountDown
                ref={countdownRef}
                initialSecond={otpData.remainingSecond}
                onCountEnd={() => {
                  setCountDownEnd(true);
                }}
                onCountStart={() => setCountDownEnd(false)}
              />
              )
            </Text>
          </If>

          <If condition={countDownEnd || otpData.remainingSecond === 0}>
            <Button
              type="borderless"
              // @ts-expect-error
              pl="0px"
              pb="16px"
              pt="5px"
              onClick={() => {
                setCountDownEnd(false);
                onResetInput();
                requestOtp();
              }}
            >
              <Text color="#00ab6b" weight="700">
                Resend
              </Text>
            </Button>
          </If>

          <If condition={multipleOTPMethodAvailable}>
            <Text
              color={color.primary}
              weight="700"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowModalOptions(true)}
            >
              Try Another Way
            </Text>
          </If>
        </div>

        <Button
          block
          type="primary"
          height="44px"
          radius="4px"
          onClick={onSubmit}
          disabled={preventSubmit}
          // @ts-expect-error
          mt="auto"
          mb="0px"
        >
          <b>{submitText}</b>
        </Button>
      </Flex>
    </>
  );
};

export default SectionOTPField;
